<template>
  <div>
    <b-card no-body class="mb-0">
      <div class="m-2">
        <b-row align-v="center">
          <b-col cols="12" md="5">
            <h4 class="mb-0">
              <feather-icon icon="AlertTriangleIcon" class="text-warning mr-1" />
              PNs com pendências
            </h4>
            <small class="text-muted">
              {{ filteredPns.length }} pendência{{ filteredPns.length !== 1 ? 's' : '' }}
            </small>
          </b-col>
          <b-col cols="12" md="7" class="mt-1 mt-md-0">
            <div class="d-flex align-items-center justify-content-end flex-wrap" style="gap:0.5rem">
              <!-- Scope toggle (afeta apenas cadastro pendente) -->
              <b-button-group size="sm">
                <b-button
                  :variant="scope === 'structure' ? 'primary' : 'outline-primary'"
                  @click="setScope('structure')"
                >
                  <feather-icon icon="GitBranchIcon" size="12" class="mr-25" />
                  Estrutura
                </b-button>
                <b-button
                  :variant="scope === 'direct' ? 'primary' : 'outline-primary'"
                  @click="setScope('direct')"
                >
                  <feather-icon icon="UsersIcon" size="12" class="mr-25" />
                  Equipe direta
                </b-button>
              </b-button-group>

              <!-- Type filter -->
              <b-form-select
                v-model="typeFilter"
                :options="typeOptions"
                size="sm"
                style="max-width:170px"
              />

              <!-- Urgency filter -->
              <b-form-select
                v-model="urgencyFilter"
                :options="urgencyOptions"
                size="sm"
                style="max-width:180px"
              />

              <!-- Status filter (manuais) -->
              <b-form-select
                v-model="statusFilter"
                :options="statusOptions"
                size="sm"
                style="max-width:210px"
              />

              <b-button
                variant="outline-primary"
                size="sm"
                @click="refresh"
                :disabled="loading"
              >
                <feather-icon icon="RefreshCwIcon" size="14" class="mr-50" />
                Atualizar
              </b-button>
            </div>
          </b-col>
        </b-row>
        <b-row align-v="center" class="mt-1">
          <b-col cols="12" md="7" offset-md="5">
            <div class="d-flex align-items-center justify-content-end flex-wrap" style="gap:0.5rem">
              <small class="text-muted mr-25">Escritório/Nível filtram só manuais:</small>
              <!-- Physical franchise filter (manuais) -->
              <b-form-select
                v-model="physicalFranchiseFilter"
                :options="physicalFranchiseOptions"
                size="sm"
                style="max-width:200px"
                @change="refresh"
              />
              <!-- Consultant role/level filter (manuais) -->
              <b-form-select
                v-model="roleFilter"
                :options="roleOptions"
                size="sm"
                style="max-width:200px"
                @change="refresh"
              />
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="table"
        :items="filteredPns"
        :fields="fields"
        :tbody-tr-class="rowClass"
        :busy="loading"
        responsive
        show-empty
        empty-text="Nenhuma pendência encontrada."
        :details-td-classes="'p-0'"
      >
        <!-- Type badge -->
        <template #cell(pendency_type)="{ item }">
          <b-badge :variant="item.pendency_type === 'manuais' ? 'light-info' : 'light-secondary'">
            {{ item.pendency_type === 'manuais' ? 'Manuais' : 'Cadastro' }}
          </b-badge>
        </template>

        <!-- Urgency badge -->
        <template #cell(urgency)="{ item }">
          <b-badge :variant="urgencyVariant(item.urgency)" pill>
            {{ urgencyLabel(item.urgency) }}
          </b-badge>
        </template>

        <!-- Status -->
        <template #cell(status)="{ item }">
          <b-badge :variant="statusVariant(item.status)">
            {{ statusLabel(item) }}
          </b-badge>
          <feather-icon
            v-if="item.status === 'near_deadline'"
            v-b-tooltip.hover
            title="Prazo próximo do vencimento"
            icon="AlertTriangleIcon"
            size="14"
            class="text-warning ml-50"
          />
        </template>

        <!-- Days remaining -->
        <template #cell(days_remaining)="{ item }">
          <span :class="daysClass(item.days_remaining)" class="font-weight-bold">
            {{ daysRemainingLabel(item) }}
          </span>
        </template>

        <!-- Missing count -->
        <template #cell(missing_count)="{ item }">
          <b-badge variant="light-danger">
            {{ item.missing_count }}
          </b-badge>
        </template>

        <!-- Actions -->
        <template #cell(actions)="{ item, toggleDetails, detailsShowing }">
          <div class="d-flex align-items-center flex-wrap" style="gap:0.25rem">
            <b-button
              size="sm"
              variant="flat-secondary"
              @click="toggleDetails"
            >
              <feather-icon :icon="detailsShowing ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" />
              {{ detailsShowing ? 'Ocultar' : 'Ver itens' }}
            </b-button>
            <b-button
              size="sm"
              variant="flat-primary"
              @click="viewProfile(item)"
            >
              <feather-icon icon="ExternalLinkIcon" size="14" class="mr-50" />
              Perfil
            </b-button>
            <b-button
              v-if="item.cell_phone && item.pendency_type === 'cadastro'"
              size="sm"
              variant="flat-success"
              @click="openWhatsApp(item)"
            >
              <feather-icon icon="MessageCircleIcon" size="14" class="mr-50" />
              WhatsApp
            </b-button>
          </div>
        </template>

        <!-- Row details: expanded checklist -->
        <template #row-details="{ item }">
          <div class="px-2 py-1 bg-light-secondary">
            <p class="font-weight-bold mb-75 small text-uppercase text-muted">
              Itens pendentes
            </p>
            <ul class="list-unstyled mb-0">
              <li
                v-for="field in item.missing_fields"
                :key="field.field"
                class="d-flex align-items-center mb-50"
              >
                <feather-icon icon="XCircleIcon" size="14" class="text-danger mr-75" />
                <span class="small">{{ field.label }}</span>
              </li>
            </ul>
          </div>
        </template>

        <template #table-busy>
          <div class="text-center text-primary my-3">
            <b-spinner class="align-middle mr-1" />
            <strong>Carregando...</strong>
          </div>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BBadge, BButton, BButtonGroup,
  BFormSelect, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import * as types from '@/modules/consultant/store/types'
import * as sharedTypes from '@/modules/shared/store/types'
import * as franchiseManualTypes from '@/modules/franchise_manual/store/types'
import { createWppPnOnboardingMessage } from '@/helpers/whatsapp_messages'

export default {
  name: 'PnOnboardingPanelList',
  components: {
    BCard, BRow, BCol, BTable, BBadge, BButton, BButtonGroup, BFormSelect, BSpinner,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      loading: false,
      scope: 'structure',
      urgencyFilter: 'all',
      typeFilter: 'all',
      statusFilter: 'all',
      physicalFranchiseFilter: '',
      roleFilter: '',
      manualCompliance: [],
      urgencyOptions: [
        { value: 'all',      text: 'Todos os níveis'  },
        { value: 'critical', text: 'Crítico (≤1 dia)'  },
        { value: 'high',     text: 'Alto (≤3 dias)'   },
        { value: 'medium',   text: 'Médio (≤7 dias)'  },
      ],
      typeOptions: [
        { value: 'all',      text: 'Todas as pendências' },
        { value: 'cadastro', text: 'Cadastro pendente'   },
        { value: 'manuais',  text: 'Manuais pendentes'   },
      ],
      // Status é específico de manuais — cadastro pendente não tem esses estados.
      statusOptions: [
        { value: 'all',               text: 'Qualquer status (pendências)' },
        { value: 'awaiting_receipt',  text: 'Aguardando recebimento'       },
        { value: 'pending_reading',   text: 'Leitura pendente'             },
        { value: 'near_deadline',     text: 'Próximo do vencimento'        },
        { value: 'expired',           text: 'Prazo vencido'                },
        { value: 'blocked',           text: 'Acesso bloqueado'             },
        { value: 'send_failed',       text: 'Falha no envio'               },
        { value: 'reading_confirmed', text: 'Leitura concluída'            },
      ],
      fields: [
        { key: 'pendency_type',  label: 'Tipo',           sortable: true  },
        { key: 'urgency',        label: 'Urgência',       sortable: true  },
        { key: 'status',         label: 'Status',         sortable: true  },
        { key: 'days_remaining', label: 'Prazo restante', sortable: true  },
        { key: 'name',           label: 'Nome',           sortable: true  },
        { key: 'email',          label: 'E-mail',         sortable: false },
        { key: 'deadline_date',  label: 'Prazo',          sortable: false },
        { key: 'missing_count',  label: 'Itens',          sortable: true  },
        { key: 'actions',        label: 'Ações',          sortable: false },
      ],
    }
  },
  computed: {
    ...mapGetters({
      pns: types.PNS_PENDING_ONBOARDING,
      physicalFranchises: sharedTypes.PHYSICAL_FRANCHISES,
      consultantRoles: sharedTypes.ROLES,
    }),
    physicalFranchiseOptions() {
      const options = (this.physicalFranchises || []).map(f => ({ value: f.id, text: f.name }))
      return [{ value: '', text: 'Todos os escritórios' }, ...options]
    },
    roleOptions() {
      const options = (this.consultantRoles || []).map(r => ({ value: r.id_consultant_role, text: r.name }))
      return [{ value: '', text: 'Todos os níveis' }, ...options]
    },
    // Escritório/nível só existem para manuais (via Consultant/ConsultantNetwork) —
    // cadastro pendente não tem esse dado, então some da lista quando filtrado.
    officeOrRoleFilterActive() {
      return !!this.physicalFranchiseFilter || !!this.roleFilter
    },
    allPendencies() {
      const cadastro = this.officeOrRoleFilterActive
        ? []
        : (this.pns || []).map(p => ({ ...p, pendency_type: 'cadastro' }))
      // Sem status definido, esconde leitura concluída e envios pendentes.
      const manuais = (this.manualCompliance || [])
        .filter(m => {
          if (this.statusFilter !== 'all') return m.status === this.statusFilter
          return m.status !== 'reading_confirmed' && m.status !== 'awaiting_send'
        })
        .map(m => ({ ...m, pendency_type: 'manuais' }))
      return [...cadastro, ...manuais]
    },
    filteredPns() {
      let items = this.allPendencies
      if (this.typeFilter !== 'all') {
        items = items.filter(p => p.pendency_type === this.typeFilter)
      }
      if (this.urgencyFilter !== 'all') {
        items = items.filter(p => p.urgency === this.urgencyFilter)
      }
      return items
    },
  },
  methods: {
    ...mapActions({
      fetchPns: types.GET_PNS_PENDING_ONBOARDING,
      searchManualCompliance: franchiseManualTypes.SEARCH_FRANCHISE_MANUAL_COMPLIANCE,
      fetchPhysicalFranchises: sharedTypes.GET_PHYSICAL_FRANCHISES,
      fetchRoles: sharedTypes.GET_ROLES,
    }),
    async refresh() {
      this.loading = true
      try {
        await Promise.all([
          this.fetchPns(this.scope),
          this.loadManualCompliance(),
        ])
      } finally {
        this.loading = false
      }
    },
    loadManualCompliance() {
      return this.searchManualCompliance({
        limit: 100,
        physical_franchise_id: this.physicalFranchiseFilter || undefined,
        id_consultant_role: this.roleFilter || undefined,
      })
        .then((resp) => {
          this.manualCompliance = resp.data?.data || []
        })
        .catch(() => {
          this.manualCompliance = []
        })
    },
    async setScope(newScope) {
      if (this.scope === newScope) return
      this.scope = newScope
      await this.refresh()
    },
    viewProfile(item) {
      this.$router.push({
        name: 'consultant-details-summary',
        params: { id: item.appify_consultant_id },
      })
    },
    openWhatsApp(item) {
      const msg = createWppPnOnboardingMessage(
        item.name,
        item.days_remaining,
        item.missing_fields.map(f => f.label),
        item.deadline_date
      )
      window.open(`https://wa.me/${item.cell_phone}?text=${msg}`, '_blank').focus()
    },
    urgencyVariant(urgency) {
      return { critical: 'danger', high: 'warning', medium: 'info' }[urgency] || 'secondary'
    },
    urgencyLabel(urgency) {
      return { critical: 'Crítico', high: 'Alto', medium: 'Médio' }[urgency] || urgency
    },
    statusVariant(status) {
      return {
        near_deadline: 'warning',
        expired: 'danger',
        blocked: 'danger',
        send_failed: 'danger',
        awaiting_receipt: 'light-warning',
        pending_reading: 'light-info',
        awaiting_send: 'light-secondary',
        reading_confirmed: 'light-success',
      }[status] || 'light-secondary'
    },
    statusLabel(item) {
      if (item.pendency_type === 'cadastro') return 'Cadastro incompleto'
      return {
        near_deadline: 'Prazo próximo do vencimento',
        expired: 'Prazo vencido',
        blocked: 'Acesso bloqueado',
        send_failed: 'Falha no envio',
        awaiting_receipt: 'Aguardando recebimento',
        pending_reading: 'Leitura pendente',
        awaiting_send: 'Aguardando envio',
        reading_confirmed: 'Leitura concluída',
      }[item.status] || (item.status || '—')
    },
    // Destaca a linha de quem está perto de vencer ou já venceu, para não
    // depender só de ler a coluna de status.
    rowClass(item, type) {
      if (!item || type !== 'row') return ''
      if (['expired', 'blocked', 'send_failed'].includes(item.status)) return 'pn-row-alert-danger'
      if (item.status === 'near_deadline') return 'pn-row-alert-warning'
      return ''
    },
    daysClass(days) {
      if (days === null || days === undefined) return 'text-muted'
      if (days <= 1) return 'text-danger'
      if (days <= 3) return 'text-warning'
      return 'text-info'
    },
    // Prefere o texto pronto do backend ("Vence hoje", "Vencido há 2 dias") —
    // dia negativo em tela não diz nada para quem está cobrando.
    daysRemainingLabel(item) {
      if (item.deadline_label) return item.deadline_label
      const days = item.days_remaining
      if (days === null || days === undefined) return '—'
      if (days < 0) return `Vencido há ${Math.abs(days)} dia${days !== -1 ? 's' : ''}`
      if (days === 0) return 'Vence hoje'
      return `${days} dia${days !== 1 ? 's' : ''}`
    },
  },
  created() {
    this.fetchPhysicalFranchises().catch(() => {})
    this.fetchRoles().catch(() => {})
    this.refresh()
  },
}
</script>

<style lang="scss" scoped>
.bg-light-secondary {
  border-top: 1px solid #ebe9f1;
}
</style>

<!-- Não pode ser scoped: as <tr> são renderizadas dentro do b-table. -->
<style lang="scss">
// Destaque de prazo: barra lateral + fundo bem leve, para o texto manter o
// contraste do tema (table-warning/table-danger do Bootstrap derrubavam).
.pn-row-alert-warning > td {
  background-color: rgba(255, 159, 67, 0.09) !important;
  border-top-color: rgba(255, 159, 67, 0.25) !important;
}
.pn-row-alert-warning:hover > td {
  background-color: rgba(255, 159, 67, 0.16) !important;
}
.pn-row-alert-warning > td:first-child {
  box-shadow: inset 4px 0 0 0 #ff9f43;
}

.pn-row-alert-danger > td {
  background-color: rgba(234, 84, 85, 0.09) !important;
  border-top-color: rgba(234, 84, 85, 0.25) !important;
}
.pn-row-alert-danger:hover > td {
  background-color: rgba(234, 84, 85, 0.16) !important;
}
.pn-row-alert-danger > td:first-child {
  box-shadow: inset 4px 0 0 0 #ea5455;
}
</style>
